// apps/web/src/components/ui/Modal.tsx
//
// Modal dialog. Traps focus, dismisses on Escape, returns focus to
// the trigger on close. Use for destructive-action confirms (revoke
// session, change account status, delete account) — the spec
// explicitly requires a confirm step for these (phase-1-5 §7).

import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  children?: ReactNode;
  primaryAction?: ReactNode;
  secondaryAction?: ReactNode;
  /** Set to true to mark the action as destructive (red header). */
  destructive?: boolean;
}

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  primaryAction,
  secondaryAction,
  destructive = false,
}: ModalProps): JSX.Element | null {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocus = useRef<Element | null>(null);

  useEffect(() => {
    if (!open) return;
    previousFocus.current = document.activeElement;
    // Focus the dialog so Escape works immediately.
    requestAnimationFrame(() => {
      const first = dialogRef.current?.querySelector<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      first?.focus();
    });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (previousFocus.current instanceof HTMLElement) {
        previousFocus.current.focus();
      }
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="presentation"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        className="w-full max-w-md rounded-lg bg-white shadow-xl dark:bg-gray-900"
      >
        <div className={`px-5 py-4 ${destructive ? 'border-b border-red-200 dark:border-red-900' : 'border-b border-gray-200 dark:border-gray-800'}`}>
          <h2
            id="modal-title"
            className={`text-lg font-semibold ${destructive ? 'text-red-700 dark:text-red-300' : 'text-gray-900 dark:text-gray-100'}`}
          >
            {title}
          </h2>
          {description ? (
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{description}</p>
          ) : null}
        </div>
        <div className="px-5 py-4 text-sm text-gray-800 dark:text-gray-200">
          {children}
        </div>
        {(primaryAction || secondaryAction) && (
          <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-3 dark:border-gray-800">
            {secondaryAction}
            {primaryAction}
          </div>
        )}
      </div>
    </div>
  );
}
